import api from './apiClient';
import type { StreamChannel } from './channelsService';

type RouteTarget =
  | number
  | string
  | {
      id?: number | string | null;
      hashId?: string | null;
      channelHashId?: string | null;
      transcoderHashId?: string | null;
    };

export interface PlatformTarget {
  id: number;
  hashId?: string;
  displayId?: string;
  channelHashId?: string;
  channelDisplayId?: string;
  transcoderHashId?: string;
  transcoderDisplayId?: string;
  channelId: number;
  broadcaster: string;
  protocol: string;
  destination: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  status?: 'connected' | 'disconnected' | 'error' | 'connecting';
}

export interface StreamStats {
  streamId: string | null;
  streamName: string;
  channelName: string;
  streamKeyPin?: string;
  type: 'rtmpConn' | 'srtConn' | null;
  sourceType?: string | null;
  sourceMode?: 'LIVE_STUDIO' | 'PLAYKASTR' | 'MEDIABOX';
  sourceLabel?: string | null;
  isMediaBoxSource?: boolean;
  isPlayKastrSource?: boolean;
  videoCodec: string | null;
  audioCodec: string | null;
  timeStarted: string | null;
  packetsSent?: number;
  packetsSentLoss?: number;
  packetsReceived?: number;
  packetsReceivedLoss?: number;
  packetsReceivedLossRate?: number;
  msRTT?: number;
  msReceiveTsbPdDelay?: number;
  remoteAddr?: string;
  frameRate?: string;
  bitrateEnforcement?: {
    currentBitrateKbps: number;
    limitKbps: number | null;
    warningActive: boolean;
    warningStartedAt?: string | null;
    kickAt?: string | null;
    secondsRemaining?: number | null;
    lastCheckedAt?: string | null;
    kickNoticeActive?: boolean;
    kickReason?: 'initial_over_limit' | 'recurring_over_limit' | string | null;
    kickedAt?: string | null;
  } | null;
}

export interface StreamAnalytics {
  streamName: string;
  bitrateKbps: number;
  viewers: number;
  uptimeSec: number;
  startTime: string | null;
  bitrateEnforcement?: StreamStats['bitrateEnforcement'];
}

export interface Payment {
  id: number;
  amount: number;
  date: string;
  status: string;
  currency: string;
  description: string | null;
  displayDescription?: string;
  transactionId?: string;
}

export type DeliveryMode = 'PUSH' | 'PULL';
export type PullProtocol = 'SRT' | 'RIST';
export type TranscoderRuntimeState = 'connecting' | 'running' | 'retrying' | 'idle' | 'error' | 'failed' | 'connected' | 'disabled';
export type DestinationHealthStatus = 'connecting' | 'connected' | 'disconnected' | 'disabled';

export interface DestinationHealthMetrics {
  ip: string | null;
  trafficKbps: number;
  dataMoving: boolean;
  validIp: boolean;
  positiveTraffic: boolean;
  updatedAt?: string | null;
  source?: string;
}

export interface DestinationHealthEnvelope {
  outputId: number;
  outputHashId?: string;
  outputDisplayId?: string;
  status: DestinationHealthStatus;
  listenerState?: 'waiting' | 'connected' | 'disconnected';
  metrics: DestinationHealthMetrics;
  raw?: any;
}

export interface ChannelUpdatePayload {
  name?: string;
  isActive?: boolean;
}

export interface UpdateTranscoderPayload {
  platformDistributor?: string | null;
  regionLocation?: string | null;
  protocol?: 'rtmp' | 'srt' | 'rist' | 'hls';
  destination?: string;
  streamId?: string | null;
  passphrase?: string | null;
  secret?: string | null;
  videoCodec?: string;
  audioCodec?: string;
  isActive?: boolean;
  isSocialStream?: boolean;
  isManagedService?: boolean;
  videoBitrate?: string | null;
  audioBitrate?: string | null;
  resolution?: string | null;
  aspectRatio?: string | null;
  frameRate?: string | null;
  keyframeInterval?: string | null;
  bitrateMode?: string | null;
  crfValue?: string | null;
  transcodePreset?: string | null;
  transcodeProfile?: string | null;
  transcodeLevel?: string | null;
  audioSampleRate?: string | null;
  audioChannels?: string | null;
  deinterlace?: string | null;
  scaleFilter?: string | null;
  loudnorm?: boolean;
  loudnormTarget?: string | null;
  logoOverlayEnabled?: boolean;
  logoOverlayPosition?: string | null;
  logoOverlayOpacity?: number | null;
  deliveryMode?: DeliveryMode;
  pullLabel?: string | null;
  pullProtocol?: PullProtocol;
  pullBufferMs?: number | null;
  pullLatencyMs?: number | null;
}

interface TranscoderBase {
  id: number;
  hashId?: string;
  displayId?: string;
  channelHashId?: string;
  channelDisplayId?: string;
  transcoderHashId?: string;
  transcoderDisplayId?: string;
  userId?: number;
  channelId: number;
  isActive: boolean;
  protocol: 'rtmp' | 'srt' | 'rist' | 'hls';
  destination: string;
  streamId?: string | null;
  passphrase?: string | null;
  secret?: string | null;
  videoCodec: string;
  audioCodec: string;
  videoBitrate?: string | null;
  audioBitrate?: string | null;
  resolution?: string | null;
  aspectRatio?: string | null;
  frameRate?: string | null;
  keyframeInterval?: string | null;
  bitrateMode?: string | null;
  crfValue?: string | null;
  transcodePreset?: string | null;
  transcodeProfile?: string | null;
  transcodeLevel?: string | null;
  audioSampleRate?: string | null;
  audioChannels?: string | null;
  deinterlace?: string | null;
  scaleFilter?: string | null;
  loudnorm?: boolean;
  loudnormTarget?: string | null;
  logoOverlayEnabled?: boolean;
  logoOverlayPosition?: string | null;
  logoOverlayOpacity?: number | null;
  platformDistributor?: string | null;
  regionLocation?: string | null;
  enableScte35?: boolean;
  isHlsSpecial?: boolean;
  isSocialStream?: boolean;
  isManagedService?: boolean;

  deliveryMode?: DeliveryMode;
  isPullLink?: boolean;
  pullLabel?: string | null;
  pullPort?: number | null;
  pullKey?: string | null;
  pullSecret?: string | null;
  pullProtocol?: PullProtocol;
  pullBufferMs?: number | null;
  pullLatencyMs?: number | null;

  hlsMount?: string | null;
  playbackUrl?: string | null;
  hlsWorkerMode?: 'SBA' | null;
  hlsConfig?: {
    abrEnabled?: boolean;
    scte35Enabled?: boolean;
    segmentDuration?: number;
    playlistLength?: number;
    videoCodec?: string;
    audioCodec?: string;
  } | null;

  createdAt?: string;
  updatedAt?: string;
}

export interface TranscoderRuntimeEnvelope {
  status: TranscoderRuntimeState;
  stats: any;
  retryCount?: number;
  lastError?: string | null;
  lastErrorTime?: string | null;
  startedAt?: string | null;
}

interface TranscoderRuntimeStatus {
  status?: TranscoderRuntimeEnvelope | null;
  retryCount?: number;
  lastError?: string | null;
  lastErrorTime?: string | null;
  startedAt?: string | null;
  destinationHealth?: DestinationHealthEnvelope | null;
}

export interface Transcoder extends TranscoderBase, TranscoderRuntimeStatus {}

export interface TranscoderStatusMap {
  [transcoderId: number]: TranscoderRuntimeEnvelope;
}

export interface PullViewerGeo {
  country?: string;
  region?: string;
  city?: string;
  isp?: string;
  org?: string;
}

export interface PullViewerInfo {
  ip: string;
  connectedAt: string;
  lastSeenAt: string;
  geo?: PullViewerGeo | null;
}

export interface PullOutputStatus {
  outputId: number;
  outputHashId?: string;
  outputDisplayId?: string;
  channelStreamId: string;
  enabled: boolean;
  processAlive: boolean;
  pulled: boolean;
  status: TranscoderRuntimeState;
  port: number;
  pullUrl: string;
  pullKey: string;
  pullSecret?: string | null;
  hasPassphrase: boolean;
  pullProtocol: PullProtocol;
  pullBufferMs?: number | null;
  pullLatencyMs?: number | null;
  lastBytesOut: number;
  totalBytesOut: number;
  lastActivityAt?: string | null;
  startedAt?: string | null;
  currentKbps: number;
  averageKbps: number;
  peakKbps: number;
  viewerCount: number;
  currentViewer?: PullViewerInfo | null;
  lastViewerIp?: string | null;
  lastViewerSeen?: string | null;
}

export interface PullOutputStatusEnvelope {
  outputId: number;
  outputHashId?: string;
  outputDisplayId?: string;
  deliveryMode: DeliveryMode;
  isActive: boolean;
  pullLabel: string;
  pullPort: number | null;
  pullKey: string | null;
  pullSecret?: string | null;
  hasPassphrase: boolean;
  pullProtocol: PullProtocol;
  pullBufferMs?: number | null;
  pullLatencyMs?: number | null;
  pullUrl?: string | null;
  destinationHealth?: DestinationHealthEnvelope | null;
  status: PullOutputStatus;
}

const resolveRouteIdFromTarget = (
  value: RouteTarget,
  hashKeys: Array<'channelHashId' | 'transcoderHashId' | 'hashId'>,
  fallbackHashId?: string | null
) => {
  if (typeof value === 'object' && value !== null) {
    for (const key of hashKeys) {
      const next = value[key];
      if (next) return String(next);
    }
    if (value.id !== undefined && value.id !== null) return String(value.id);
  }
  return String(fallbackHashId || value);
};

const resolveChannelRouteId = (value: RouteTarget, fallbackHashId?: string | null) =>
  resolveRouteIdFromTarget(value, ['channelHashId', 'hashId'], fallbackHashId);

const resolveTranscoderRouteId = (value: RouteTarget, fallbackHashId?: string | null) =>
  resolveRouteIdFromTarget(value, ['transcoderHashId', 'hashId'], fallbackHashId);

export interface HlsSessionStatusActiveSession {
  clientIp: string;
  userAgent?: string | null;
  requestCount: number;
  playlistRequests: number;
  segmentRequests: number;
  bytesServed: number;
  peakKbps: number;
  averageKbps: number;
  country?: string | null;
  region?: string | null;
  city?: string | null;
  isp?: string | null;
  startedAt: string;
  lastSeenAt: string;
  status: string;
}

export interface HlsSessionStatusEnvelope {
  outputId: number;
  outputHashId?: string;
  outputDisplayId?: string;
  hlsMount?: string | null;
  playbackUrl?: string | null;
  rev: number;
  destinationHealth?: DestinationHealthEnvelope | null;
  activeSession?: HlsSessionStatusActiveSession | null;
}

export interface RotatePullResult {
  id: number;
  hashId?: string;
  displayId?: string;
  channelHashId?: string;
  channelDisplayId?: string;
  transcoderHashId?: string;
  transcoderDisplayId?: string;
  channelId: number;
  pullPort?: number | null;
  pullKey?: string | null;
  pullSecret?: string | null;
  pullProtocol?: PullProtocol;
  pullBufferMs?: number | null;
  pullLatencyMs?: number | null;
  updatedAt?: string;
}

export interface DisconnectPullResult {
  ok: boolean;
  outputId: number;
  rotated?: {
    pullPort?: number | null;
    pullKey?: string | null;
    pullSecret?: string | null;
    pullProtocol?: PullProtocol;
    pullBufferMs?: number | null;
    pullLatencyMs?: number | null;
    pullUrl?: string | null;
  };
}

export interface RuntimeHealth {
  activeSocketCount: number;
  runningTranscodersCount: number;
  streamMonitorRunning: boolean;
  heapUsed: number;
  rss: number;
  uptime: number;
}

export interface RuntimeAlarm {
  channelId: number;
  alarmKey: string;
  raised: boolean;
  entityType?: string;
  outputId?: number;
  emittedAt: string;
}

export interface RuntimeSnapshot {
  channels: StreamChannel[];
  activeChannel: StreamChannel | null;
  stats: StreamStats | null;
  analytics: StreamAnalytics | null;
  transcoders: Transcoder[];
  pullStatuses: Record<number, PullOutputStatusEnvelope>;
  hlsStatuses: Record<number, HlsSessionStatusEnvelope>;
  outputHealthStatuses: Record<number, DestinationHealthEnvelope>;
  activeAlarms?: RuntimeAlarm[];
  health: RuntimeHealth;
  serverTime: string;
}

export const updateMyPassword = async (currentPassword: string, newPassword: string): Promise<void> =>
  api.put('/users/me/password', { currentPassword, newPassword });


export const getMyPayments = async (): Promise<Payment[]> =>
  api.get<Payment[]>('/users/payments').then((res) => res.data);

export const getRuntimeSnapshot = async (channel?: RouteTarget | null): Promise<RuntimeSnapshot> =>
  api.get<RuntimeSnapshot>('/runtime/snapshot', {
    params: channel ? { channelId: resolveChannelRouteId(channel) } : undefined,
  }).then((res) => res.data);

export const getStreamStatsForChannel = async (channel: RouteTarget, hashId?: string | null): Promise<StreamStats> =>
  api.get<StreamStats>(`/streams/channels/${resolveChannelRouteId(channel, hashId)}/stats`).then((res) => res.data);

export const getStreamAnalyticsForChannel = async (channel: RouteTarget, hashId?: string | null): Promise<StreamAnalytics> =>
  api.get<StreamAnalytics>(`/streams/channels/${resolveChannelRouteId(channel, hashId)}/analytics`).then((res) => res.data);

export const getTranscoders = async (channel: RouteTarget, hashId?: string | null): Promise<Transcoder[]> =>
  api.get<Transcoder[]>(`/streams/channels/${resolveChannelRouteId(channel, hashId)}/transcoders`).then((res) => res.data);

export const createTranscoder = async (transcoderData: Partial<Transcoder>): Promise<Transcoder> =>
  api.post<Transcoder>('/streams/transcoders', transcoderData).then((res) => res.data);

export const updateTranscoder = async (target: RouteTarget, updates: UpdateTranscoderPayload, hashId?: string | null): Promise<Transcoder> =>
  api.patch<Transcoder>(`/streams/transcoders/${resolveTranscoderRouteId(target, hashId)}`, updates).then((res) => res.data);

export const rotateTranscoderPullKey = async (target: RouteTarget, hashId?: string | null): Promise<RotatePullResult> =>
  api.post<RotatePullResult>(`/streams/transcoders/${resolveTranscoderRouteId(target, hashId)}/rotate-pull-key`).then((res) => res.data);

export const rotateTranscoderPullSecret = async (target: RouteTarget, hashId?: string | null): Promise<RotatePullResult> =>
  api.post<RotatePullResult>(`/streams/transcoders/${resolveTranscoderRouteId(target, hashId)}/rotate-pull-secret`).then((res) => res.data);

export const clearTranscoderPullSecret = async (target: RouteTarget, hashId?: string | null): Promise<RotatePullResult> =>
  api.post<RotatePullResult>(`/streams/transcoders/${resolveTranscoderRouteId(target, hashId)}/clear-pull-secret`).then((res) => res.data);

export const deleteTranscoder = async (target: RouteTarget, hashId?: string | null): Promise<void> =>
  api.delete(`/streams/transcoders/${resolveTranscoderRouteId(target, hashId)}`);

export const getPullOutputStatus = async (target: RouteTarget, hashId?: string | null): Promise<PullOutputStatusEnvelope> =>
  api.get<PullOutputStatusEnvelope>(`/streams/transcoders/${resolveTranscoderRouteId(target, hashId)}/pull-status`).then((res) => res.data);

export const disconnectPullOutput = async (target: RouteTarget, hashId?: string | null): Promise<DisconnectPullResult> =>
  api.post<DisconnectPullResult>(`/streams/transcoders/${resolveTranscoderRouteId(target, hashId)}/pull-disconnect`).then((res) => res.data);

export const getDestinationHealth = async (target: RouteTarget, hashId?: string | null): Promise<DestinationHealthEnvelope> =>
  api.get<DestinationHealthEnvelope>(`/streams/transcoders/${resolveTranscoderRouteId(target, hashId)}/health`).then((res) => res.data);

export const disconnectHlsOutput = async (target: RouteTarget, hashId?: string | null): Promise<{ ok: boolean; outputId: number; playbackUrl: string }> =>
  api.post(`/streams/transcoders/${resolveTranscoderRouteId(target, hashId)}/hls-disconnect`).then(res => res.data);

export const getHlsSessionStatus = async (target: RouteTarget, hashId?: string | null): Promise<HlsSessionStatusEnvelope> =>
  api.get<HlsSessionStatusEnvelope>(`/streams/transcoders/${resolveTranscoderRouteId(target, hashId)}/hls-session-status`).then((res) => res.data);

export interface PreviewSession {
  id: string;
  mode: 'webrtc' | 'hls';
  requestedMode: 'webrtc' | 'hls' | 'auto';
  videoCodec: string;
  audioCodec: string;
  selectionReason: string;
  hlsUrl?: string;
  webrtcUrl?: string;
  expiresInSeconds: number;
}

export const createPreviewSession = async (
  channel: RouteTarget,
  preferredMode: 'webrtc' | 'hls' | 'auto',
  hashId?: string | null,
  signal?: AbortSignal,
): Promise<PreviewSession> => api.post<PreviewSession>(
  `/streams/channels/${resolveChannelRouteId(channel, hashId)}/preview/sessions`,
  { preferredMode },
  { signal },
).then((res) => res.data);

export const startPreviewWebRTC = async (webrtcUrl: string, sdpOffer: string): Promise<string> => {
  const response = await fetch(webrtcUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/sdp' },
    body: sdpOffer,
    credentials: 'same-origin',
  });
  const body = await response.text();
  if (!response.ok) {
    const error: any = new Error(body || `WebRTC preview failed with status ${response.status}.`);
    error.status = response.status;
    throw error;
  }
  return body;
};

export const revokePreviewSession = async (channel: RouteTarget, sessionId: string, hashId?: string | null): Promise<void> => {
  await api.delete(`/streams/channels/${resolveChannelRouteId(channel, hashId)}/preview/sessions/${encodeURIComponent(sessionId)}`);
};

export const getTranscoderStatuses = async (): Promise<TranscoderStatusMap> =>
  api.get<TranscoderStatusMap>('/streams/transcoders/statuses').then((res) => res.data);

export const updateChannel = async (channel: RouteTarget, updates: ChannelUpdatePayload, hashId?: string | null) =>
  api.patch(`/streams/channels/${resolveChannelRouteId(channel, hashId)}`, updates).then((res) => res.data);

export const deleteChannel = async (channel: RouteTarget, hashId?: string | null): Promise<void> =>
  api.delete(`/streams/channels/${resolveChannelRouteId(channel, hashId)}`);
